import React, { useState } from "react";
import { Skeleton, Modal, message } from "antd";
import {
  FilePdfOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import UploadCard from "../EbulletinUploadCard";
import UploadModal from "../modals/EbulletinUploadModal";

export default function FileBlock({
  block,
  isEditing,
  updateBlockProperty,
  bulletinId,
  sectionId,
}) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isFilePreviewOpen, setIsFilePreviewOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Parse block content if it's JSON (for media blocks)
  const getFileData = () => {
    if (block.url) {
      // New format - direct properties
      return {
        url: block.url,
        fileName: block.fileName || "unknown.pdf",
        fileSize: block.fileSize || 0,
        mimeType: block.mimeType || "application/pdf",
        style: block.style || {},
      };
    } else if (block.content) {
      try {
        // Try to parse as JSON first (serialized media data)
        const parsed = JSON.parse(block.content);
        if (parsed.url) {
          return parsed;
        }
        // Fallback: treat content as direct URL
        return {
          url: block.content,
          fileName: "unknown.pdf",
          fileSize: 0,
          mimeType: "application/pdf",
          style: {},
        };
      } catch (e) {
        // Not JSON, treat as direct URL
        return {
          url: block.content,
          fileName: "unknown.pdf",
          fileSize: 0,
          mimeType: "application/pdf",
          style: {},
        };
      }
    }
    return { url: "", fileName: "", fileSize: 0, mimeType: "", style: {} };
  };

  const fileData = getFileData();
  const fileUrl = fileData.url;

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "Unknown size";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Get file type from file name
  const getFileTypeFromName = (fileName) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    const typeMap = {
      pdf: "PDF File",
      doc: "Word Document",
      docx: "Word Document",
      xls: "Excel Spreadsheet",
      xlsx: "Excel Spreadsheet",
      ppt: "PowerPoint Presentation",
      pptx: "PowerPoint Presentation",
      txt: "Text File",
      zip: "Zip Archive",
      rar: "RAR Archive",
    };
    return typeMap[ext] || "File";
  };

  // Handle file upload completion
  const handleUploadFinish = (uploadResult) => {
    try {
      let newFileUrl, fileName, fileSize, mimeType;

      if (typeof uploadResult === "string") {
        newFileUrl = uploadResult;
        fileName = "uploaded_file.pdf";
      } else {
        newFileUrl = uploadResult.dataUrl || uploadResult.url;
        fileName =
          uploadResult.fileName || uploadResult.name || "uploaded_file.pdf";
        fileSize = uploadResult.fileSize || uploadResult.size || 0;
        mimeType =
          uploadResult.mimeType || uploadResult.type || "application/pdf";
      }

      const ext = fileName.split(".").pop()?.toLowerCase();

      // Validate file type (you can expand this list)
      const allowedTypes = [
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "ppt",
        "pptx",
        "txt",
      ];
      if (!allowedTypes.includes(ext)) {
        messageApi.open({
          type: "error",
          content: `File type .${ext} is not allowed. Please upload: ${allowedTypes.join(
            ", "
          )}`,
        });
        return;
      }

      // Update all file properties
      updateBlockProperty("url", newFileUrl);
      updateBlockProperty("fileName", fileName);
      updateBlockProperty("fileSize", fileSize);
      updateBlockProperty("mimeType", mimeType);

      setShowUploadModal(false);

      messageApi.open({
        type: "success",
        content: "File uploaded successfully!",
      });
    } catch (error) {
      console.error("File upload error:", error);
      messageApi.open({
        type: "error",
        content: "Failed to process uploaded file.",
      });
    }
  };

  // Handle file download
  const handleDownload = () => {
    if (fileUrl) {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileData.fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Check if the file can be previewed in an iframe
  const canPreview = () => {
    const ext = fileData.fileName.split(".").pop()?.toLowerCase();
    return ["pdf"].includes(ext); // Only PDF can be previewed in iframe
  };

  const getFileIcon = () => {
    const ext = fileData.fileName.split(".").pop()?.toLowerCase();
    // You can expand this with more file type icons
    switch (ext) {
      case "pdf":
        return <FilePdfOutlined className="file-icon pdf" />;
      case "doc":
      case "docx":
        return <FilePdfOutlined className="file-icon doc" />;
      case "xls":
      case "xlsx":
        return <FilePdfOutlined className="file-icon excel" />;
      default:
        return <FilePdfOutlined className="file-icon" />;
    }
  };

  const fileElement = (
    <div className="upload-file-wrapper">
      <div className="file-icon-wrapper">{getFileIcon()}</div>
      <div className="file-details">
        <div className="file-title" title={fileData.fileName}>
          {fileData.fileName}
        </div>
        <div className="file-meta">
          <span className="file-type">
            {getFileTypeFromName(fileData.fileName)}
          </span>
          {fileData.fileSize > 0 && (
            <>
              <span className="file-separator"> • </span>
              <span className="file-size">
                {formatFileSize(fileData.fileSize)}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="file-options" onClick={(e) => e.stopPropagation()}>
        {canPreview() && (
          <EyeOutlined
            className="file-action"
            title="Preview"
            onClick={() => setIsFilePreviewOpen(true)}
          />
        )}
        <DownloadOutlined
          className="file-action"
          title="Download"
          onClick={handleDownload}
        />
      </div>
    </div>
  );

  return (
    <div className="block-card file">
      {contextHolder}
      <UploadModal
        type="file"
        visible={showUploadModal}
        setVisible={setShowUploadModal}
        onUploadFinish={handleUploadFinish}
        bulletinId={bulletinId}
        sectionId={sectionId}
      />

      <Modal
        open={isFilePreviewOpen}
        onCancel={() => setIsFilePreviewOpen(false)}
        footer={null}
        width="80%"
        destroyOnClose
        centered
        title={`Preview: ${fileData.fileName}`}
        styles={{ body: { height: "80vh", padding: 0, overflow: "hidden" } }}
      >
        {fileUrl && canPreview() && (
          <div
            style={{
              paddingTop: "28px",
              height: "100%",
              borderRadius: "6px",
              boxSizing: "border-box",
            }}
          >
            <iframe
              src={fileUrl}
              title={fileData.fileName}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              onError={() => {
                console.error("File preview failed to load:", fileUrl);
                messageApi.open({
                  type: "error",
                  content: "Unable to preview this file.",
                });
              }}
            />
          </div>
        )}
      </Modal>

      {!fileUrl ? (
        isEditing ? (
          <UploadCard type="file" onClick={() => setShowUploadModal(true)} />
        ) : null
      ) : (
        fileElement
      )}
    </div>
  );
}
